import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { CalendarClock, Video, ExternalLink, User, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import ScheduleTeamsMeetingDialog from "@/components/ScheduleTeamsMeetingDialog";

/**
 * Overview widget: shows the next N scheduled meetings (any provider) grouped by day.
 * Data source: `meeting`-type activities whose `meta.start` is >= now.
 * Supports Reschedule (opens the ScheduleTeamsMeetingDialog in edit mode) and Cancel.
 */

const groupByDay = (items) => {
  const buckets = {};
  items.forEach((m) => {
    const iso = m.meta?.start;
    if (!iso) return;
    const d = new Date(iso);
    const dayKey = d.toISOString().slice(0, 10);
    if (!buckets[dayKey]) buckets[dayKey] = { date: d, items: [] };
    buckets[dayKey].items.push(m);
  });
  return Object.values(buckets).sort((a, b) => a.date - b.date);
};

const dayLabel = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / (24 * 60 * 60 * 1000));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return day.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

const timeLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

export default function UpcomingMeetings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { activity, lead }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/activities/upcoming-meetings?limit=15");
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const cancelMeeting = async (m) => {
    if (!window.confirm(`Cancel "${m.meta?.subject || m.lead_name}"?`)) return;
    try {
      await api.delete(`/activities/${m.id}`);
      toast.success("Meeting cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openReschedule = (m) => {
    // ScheduleTeamsMeetingDialog needs a `lead` object; synthesize a minimal one from enriched fields.
    const lead = { id: m.lead_id, name: m.lead_name || "Lead", company: m.lead_company || "", email: (m.meta?.attendees || "").split(",")[0] || "" };
    setEditing({ activity: m, lead });
  };

  if (loading) return null;

  const days = groupByDay(items);
  const empty = days.length === 0;

  return (
    <div className="glass rounded-2xl p-5" data-testid="upcoming-meetings-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-[#464EB8]" />
          <p className="text-sm font-semibold text-slate-800">Upcoming meetings</p>
        </div>
        {!empty && <span className="text-[11px] text-slate-500">{items.length} scheduled</span>}
      </div>

      {empty ? (
        <p className="text-sm text-slate-400 italic py-4 text-center">
          No upcoming meetings. Schedule one from any lead's detail panel.
        </p>
      ) : (
        <div className="space-y-4">
          {days.slice(0, 4).map((g) => (
            <div key={g.date.toISOString()}>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{dayLabel(g.date)}</p>
              <ul className="space-y-1.5">
                {g.items.map((m) => (
                  <li
                    key={m.id}
                    className="group flex items-start gap-3 p-2 rounded-lg hover:bg-white transition-colors"
                    data-testid={`upcoming-meeting-${m.id}`}
                  >
                    <div className="w-7 h-7 rounded-md bg-[#464EB8]/10 border border-[#464EB8]/20 flex items-center justify-center flex-shrink-0">
                      <Video className="w-3.5 h-3.5 text-[#464EB8]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{m.meta?.subject || m.lead_name}</p>
                      <div className="flex items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                        <span className="font-medium text-slate-600">{timeLabel(m.meta?.start)}</span>
                        {m.meta?.end && <span>→ {timeLabel(m.meta.end)}</span>}
                        <span>·</span>
                        <span className="truncate">{m.lead_name}{m.lead_company ? ` · ${m.lead_company}` : ""}</span>
                        <span className="flex items-center gap-0.5 text-slate-400">
                          <User className="w-3 h-3" /> {m.user_name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {m.meta?.teams_url && (
                        <a
                          href={m.meta.teams_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-[#464EB8] hover:text-[#3C43A0] flex items-center gap-1"
                          data-testid={`upcoming-meeting-open-${m.id}`}
                          title="Open in Teams"
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => openReschedule(m)}
                        className="w-6 h-6 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 opacity-70 group-hover:opacity-100 transition-opacity"
                        aria-label="Reschedule meeting"
                        data-testid={`upcoming-meeting-reschedule-${m.id}`}
                        title="Reschedule"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelMeeting(m)}
                        className="w-6 h-6 rounded-md hover:bg-rose-50 flex items-center justify-center text-slate-500 hover:text-rose-600 opacity-70 group-hover:opacity-100 transition-opacity"
                        aria-label="Cancel meeting"
                        data-testid={`upcoming-meeting-cancel-${m.id}`}
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <ScheduleTeamsMeetingDialog
        lead={editing?.lead}
        activity={editing?.activity}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onScheduled={load}
      />
    </div>
  );
}
